import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { useSale } from '../../../hooks/sale/useSale';
import { useCreditNoteSaleLookup } from '../../../hooks/sale/useCreditNoteSaleLookup';
import POSSearchInput from '../../POS/Fields/POSSearchInput';
import DatePicker from '../../Forms/DatePicker/DatePicker';
import BasicPagination from '../../DataTable/BasicPagination';

const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const POSCreditNoteReference = () => {
  const [selectedSale, setSelectedSale] = useState<{ id: number; name: string } | null>(null);
  const [dateTo, setDateTo] = useState(isoDate(new Date()));
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 14);
    return isoDate(date);
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [resultsOpen, setResultsOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { filteredDocuments, paginator, searchEligibleSales, fetchSaleDetail } = useCreditNoteSaleLookup();
  const { form } = useSale();

  const buildFilters = useCallback((term = searchTerm, from = dateFrom, to = dateTo) => ({
    customerId: form.customer!.id,
    dateFrom: from,
    dateTo: to,
    documentNumber: term.trim() || null,
    total: null,
  }), [dateFrom, dateTo, form.customer?.id, searchTerm]);

  useEffect(() => {
    setSelectedSale(null);
    setSearchTerm('');
    setResultsOpen(false);
    if (!form.customer?.id) return;

    const today = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 14);
    const nextTo = isoDate(today);
    const nextFrom = isoDate(from);
    setDateTo(nextTo);
    setDateFrom(nextFrom);
    searchEligibleSales({
      customerId: form.customer.id,
      dateFrom: nextFrom,
      dateTo: nextTo,
      documentNumber: null,
      total: null,
    }, 0);
  }, [form.customer?.id]);

  useEffect(() => {
    const closeResults = (event: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target as Node)) {
        setResultsOpen(false);
      }
    };
    document.addEventListener('mousedown', closeResults);
    return () => document.removeEventListener('mousedown', closeResults);
  }, []);

  const changeDate = (kind: 'from' | 'to', value: string) => {
    const nextFrom = kind === 'from' ? value : dateFrom;
    const nextTo = kind === 'to' ? value : dateTo;
    if (kind === 'from') setDateFrom(value);
    else setDateTo(value);
    setSelectedSale(null);
    setResultsOpen(true);
    if (form.customer?.id) searchEligibleSales(buildFilters('', nextFrom, nextTo), 0);
  };

  const search = useCallback((term: string) => {
    if (!form.customer?.id) return;
    setResultsOpen(true);
    searchEligibleSales(buildFilters(term), 0);
  }, [buildFilters, form.customer?.id]);

  const selectSale = (option: { id: number; name: string }) => {
    setSelectedSale(option);
    setSearchTerm(option.name);
    setResultsOpen(false);
    fetchSaleDetail(option.id);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DatePicker label="Desde" name="creditNoteDateFrom" value={dateFrom} iconKey="calendar" withMargin={false} onChange={(value) => changeDate('from', value)} />
        <DatePicker label="Hasta" name="creditNoteDateTo" value={dateTo} iconKey="calendar" withMargin={false} onChange={(value) => changeDate('to', value)} />
      </div>

      <div ref={resultsRef} className="relative">
        <POSSearchInput
          name="creditNoteSaleSearch"
          value={searchTerm}
          disabled={!form.customer?.id}
          placeholder="Buscar por número de venta..."
          onChange={(value) => {
            setSearchTerm(value);
            setSelectedSale(null);
          }}
          onDebouncedChange={search}
          onFocus={() => setResultsOpen(true)}
        />

        {resultsOpen && form.customer?.id && (
          <div className="absolute left-0 right-0 top-full z-99999 mt-2 overflow-hidden rounded-2xl border border-stroke bg-white shadow-lg dark:border-strokedark dark:bg-boxdark">
            <ul className="max-h-64 overflow-y-auto py-1" role="listbox" aria-label="Ventas elegibles">
              {filteredDocuments.length > 0 ? filteredDocuments.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selectedSale?.id === option.id}
                    onClick={() => selectSale(option)}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition-colors ${selectedSale?.id === option.id ? 'bg-primary text-white' : 'bg-white text-slate-800 hover:bg-primary/10 dark:bg-boxdark dark:text-white dark:hover:bg-meta-4'}`}
                  >
                    <span className="min-w-0 whitespace-normal break-words font-medium">{option.name}</span>
                    {selectedSale?.id === option.id && <Check className="h-4 w-4 shrink-0" />}
                  </button>
                </li>
              )) : (
                <li className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">No se encontraron ventas elegibles.</li>
              )}
            </ul>
            <BasicPagination
              currentPage={paginator.number}
              totalPages={paginator.totalPages}
              totalElements={paginator.totalElements}
              size={paginator.size}
              first={paginator.first}
              last={paginator.last}
              onPageChange={(page) => searchEligibleSales(buildFilters(), page, paginator.size)}
            />
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600 dark:border-strokedark dark:bg-meta-4 dark:text-slate-300">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Solo se mostrarán ventas emitidas al mismo cliente elegibles para esta nota de crédito.</p>
      </div>
    </div>
  );
};

export default POSCreditNoteReference;
